/**
 * <AccountSettingsTemplate> — la coque d'une page de paramètres du compte de l'élève (template).
 *
 * Ce que partagent « Paramètres », ses quatre pages de formulaire, « Mot de passe » et « Fermer mon
 * compte » : l'en-tête avec son retour, une colonne bornée (un formulaire étalé sur 1 100 px ne se
 * lit plus), et, quand la page enregistre, le bouton en pied, l'erreur du serveur au-dessus de lui,
 * l'accusé en `<Snackbar>` et la garde « Modifications non enregistrées ».
 *
 * Un enregistrement par page : le pied n'existe que si `onSubmit` est donné, et il se retire tant
 * que la page charge ou qu'elle est en erreur (il n'y a rien à envoyer).
 *
 * Contrôlé : l'envoi, son erreur, l'accusé et la garde arrivent en props.
 */
import React from "react"

import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { pushButtonBoxHeight } from "../../atoms/Buttons/Button/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Stack } from "../../atoms/containers/Stack"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { Header } from "../../molecules/Header"
import { Snackbar } from "../../molecules/Snackbar"
import { PageTemplate } from "../PageTemplate"

/** La colonne d'une page de paramètres : la largeur d'un formulaire qui se lit d'un regard. */
export const ACCOUNT_SETTINGS_CONTENT_WIDTH = 720

/** La place que le pied prend sous l'accusé : le bouton, et les marges de la bande. */
const FOOTER_HEIGHT = pushButtonBoxHeight("md") + 24

export type AccountSettingsTemplateProps = {
    title: string
    onBack: () => void
    children: React.ReactNode

    loading?: boolean
    /** La forme de la page pendant son premier chargement. Obligatoire avec `loading`. */
    skeleton?: React.ReactNode
    error?: unknown
    onRetry?: () => void

    /** Enregistre la page. Absent : pas de pied. */
    onSubmit?: () => void
    /** Envoi en cours : le bouton tourne et ne répond plus. */
    submitting?: boolean
    /** Le serveur a refusé l'envoi : son message, au-dessus du bouton. */
    submitError?: string | null
    /** L'accusé de l'enregistrement. Absent ou `null` : rien. */
    savedMessage?: string | null
    onDismissSaved?: () => void

    /** La garde de sortie, quand l'élève quitte avec des modifications. */
    leaveConfirmVisible?: boolean
    onConfirmLeave?: () => void
    onCancelLeave?: () => void
    /** Le voile ou le retour d'Android sur la garde. Absent : `onCancelLeave`. */
    onDismissLeave?: () => void
    testID?: string
}

/** Ce qu'une page de paramètres relaie telle quelle à sa coque. */
export type AccountSettingsShellProps = Omit<AccountSettingsTemplateProps, "title" | "children" | "skeleton">

const noop = () => {}

export function AccountSettingsTemplate(props: AccountSettingsTemplateProps) {
    const {
        title,
        onBack,
        children,
        loading = false,
        skeleton,
        error,
        onRetry,
        onSubmit,
        submitting = false,
        submitError,
        savedMessage,
        onDismissSaved,
        leaveConfirmVisible = false,
        onConfirmLeave,
        onCancelLeave,
        onDismissLeave,
        testID,
    } = props

    const hasFooter = onSubmit != null && !loading && error == null

    const header = (
        <Header
            title={title}
            left={
                <IconButton
                    iconName="chevron-left"
                    appearance="transparent"
                    iconColor="primary"
                    onPress={onBack}
                    accessibilityLabel="Retour"
                />
            }
        />
    )

    const footer = hasFooter ? (
        <Stack gap="2">
            {submitError != null && submitError !== "" && (
                <Banner message={submitError} color="danger" icon="exclamation-circle" tinted />
            )}
            <Button label="Enregistrer" fullWidth loading={submitting} onPress={onSubmit} />
        </Stack>
    ) : undefined

    return (
        <PageTemplate
            testID={testID}
            header={header}
            loading={loading}
            skeleton={skeleton}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={ACCOUNT_SETTINGS_CONTENT_WIDTH}
            footer={footer}
        >
            {children}

            <Snackbar
                visible={savedMessage != null}
                message={savedMessage ?? ""}
                onDismiss={onDismissSaved ?? noop}
                bottomOffset={hasFooter ? FOOTER_HEIGHT : 0}
            />

            <ConfirmDialog
                visible={leaveConfirmVisible}
                title="Modifications non enregistrées"
                message="Veux-tu vraiment quitter la page ?"
                confirmLabel="Quitter"
                cancelLabel="Non, continuer"
                danger={false}
                onConfirm={onConfirmLeave ?? noop}
                onCancel={onCancelLeave ?? noop}
                onDismiss={onDismissLeave ?? onCancelLeave ?? noop}
            />
        </PageTemplate>
    )
}
